/**
 * DD-LINE-ID-CLEANUP-001 G1/G2 — stored line_key shape (matches DdWorkflowLineAllocator.mintLineKey).
 *
 * Identity vs display across this module:
 * - Identity: `lineKey` / `lineKeySource` / `payloadSlot` attached onto subjects.
 * - Display: `sku` stays the catalog / product label and is never used as the mutation key.
 * - Live mint without server coverage → `lineKeySource: 'provisional'` (wire via apiLineToken).
 * - Fixture/demo mint → `lineKeySource: 'fixture'` (minted key IS identity for in-memory rows).
 */

import type { LineKeySource, Master, Request, Single, Subject } from '@/lib/dd-queue/types'
import type { DesignDevelopmentQueueIntakeLine } from '@/services/designDevelopmentQueueIntakeApi'
import type { DdWorkflowLineSnapshot } from '@/services/designDevelopmentWorkflowApi'

/** Mint allocator-shaped identity key `{originRef}-{NN}` (display sku unchanged). */
export function mintStoredLineKey(originRef: string, ordinal: number): string {
  const base = originRef.trim() || 'NOREF'
  return `${base}-${String(Math.max(1, ordinal)).padStart(2, '0')}`
}

export interface LineKeyAttachFailure {
  requestId: string
  payloadSlot: number
  sku: string | null
  reason: 'no_intake_line' | 'no_hydrate_snapshot' | 'slot_mismatch'
}

export interface LineKeyAttachResult {
  request: Request
  failures: LineKeyAttachFailure[]
}

function slotForChildIndex(indexZeroBased: number): number {
  return indexZeroBased + 1
}

function withLineKey<T extends Subject | Single>(
  subject: T,
  lineKey: string,
  source: LineKeySource,
  payloadSlot?: number
): T {
  return {
    ...subject,
    lineKey,
    lineKeySource: source,
    ...(payloadSlot != null ? { payloadSlot } : {}),
  }
}

/** payload_slot → line_ordinal → array position (Correction 1 precedence). Identity match only. */
export function resolveIntakeLineForSlot(
  lines: DesignDevelopmentQueueIntakeLine[],
  payloadSlot: number,
  indexZeroBased: number
): DesignDevelopmentQueueIntakeLine | undefined {
  const bySlot = lines.find(
    (l) => typeof l.payload_slot === 'number' && l.payload_slot === payloadSlot
  )
  if (bySlot) return bySlot

  const byOrdinal = lines.find(
    (l) =>
      (l.payload_slot == null || l.payload_slot <= 0) &&
      typeof l.line_ordinal === 'number' &&
      l.line_ordinal === payloadSlot
  )
  if (byOrdinal) return byOrdinal

  return lines[indexZeroBased]
}

/** Same precedence as intake attach — identity match on hydrate snapshots. */
export function resolveSnapshotForSlot(
  snapshots: DdWorkflowLineSnapshot[],
  payloadSlot: number,
  indexZeroBased: number
): DdWorkflowLineSnapshot | undefined {
  const bySlot = snapshots.find(
    (s) => typeof s.payload_slot === 'number' && s.payload_slot === payloadSlot
  )
  if (bySlot) return bySlot

  const byOrdinal = snapshots.find(
    (s) =>
      (s.payload_slot == null || s.payload_slot <= 0) &&
      typeof s.line_ordinal === 'number' &&
      s.line_ordinal === payloadSlot
  )
  if (byOrdinal) return byOrdinal

  return snapshots[indexZeroBased]
}

/** Find subject by identity lineKey (not display sku). */
export function findSubjectByLineKey(
  req: Request,
  lineKey: string
): Subject | Single | undefined {
  if (req.isMaster) {
    return req.children.find((c) => c.lineKey === lineKey)
  }
  const single = req as Single
  return single.lineKey === lineKey ? single : undefined
}

/** All identity lineKeys on a request (skips subjects missing lineKey). */
export function lineKeysForRequest(req: Request): string[] {
  if (req.isMaster) {
    return req.children.map((c) => c.lineKey).filter((k): k is string => !!k)
  }
  const single = req as Single
  return single.lineKey ? [single.lineKey] : []
}

/**
 * Primary: queue-intake `lines[]`. Attaches identity lineKey + source marker.
 * Returns failures for stop-and-report (`no_intake_line` → provisional mint).
 */
export function attachIntakeLineKeys(
  req: Request,
  intakeLines: DesignDevelopmentQueueIntakeLine[] | undefined
): LineKeyAttachResult {
  if (intakeLines === undefined) {
    return { request: mintFixtureLineKeys(req), failures: [] }
  }

  const failures: LineKeyAttachFailure[] = []

  if (intakeLines.length === 0) {
    const minted = mintFixtureLineKeys(req, 'provisional')
    if (req.isMaster) {
      for (let i = 0; i < req.children.length; i++) {
        failures.push({
          requestId: req.id,
          payloadSlot: slotForChildIndex(i),
          sku: req.children[i]!.sku,
          reason: 'no_intake_line',
        })
      }
    } else {
      failures.push({
        requestId: req.id,
        payloadSlot: 1,
        sku: (req as Single).sku,
        reason: 'no_intake_line',
      })
    }
    return { request: minted, failures }
  }

  if (req.isMaster) {
    const children = req.children.map((child, index) => {
      const payloadSlot = slotForChildIndex(index)
      const line = resolveIntakeLineForSlot(intakeLines, payloadSlot, index)
      if (!line?.line_key) {
        failures.push({
          requestId: req.id,
          payloadSlot,
          sku: child.sku,
          reason: 'no_intake_line',
        })
        return withLineKey(
          child,
          mintStoredLineKey(req.id, payloadSlot),
          'provisional',
          payloadSlot
        )
      }
      if (
        typeof line.payload_slot === 'number' &&
        line.payload_slot > 0 &&
        line.payload_slot !== payloadSlot
      ) {
        failures.push({
          requestId: req.id,
          payloadSlot,
          sku: child.sku,
          reason: 'slot_mismatch',
        })
      }
      return withLineKey(
        child,
        line.line_key,
        'server',
        line.payload_slot ?? payloadSlot
      )
    })
    return { request: { ...req, children }, failures }
  }

  const single = req as Single
  const line = resolveIntakeLineForSlot(intakeLines, 1, 0)
  if (!line?.line_key) {
    failures.push({
      requestId: req.id,
      payloadSlot: 1,
      sku: single.sku,
      reason: 'no_intake_line',
    })
    return {
      request: withLineKey(single, mintStoredLineKey(req.id, 1), 'provisional', 1),
      failures,
    }
  }
  return {
    request: withLineKey(single, line.line_key, 'server', line.payload_slot ?? 1),
    failures,
  }
}

/**
 * Hydrate fallback / verification when intake lines absent or incomplete.
 * Server snapshot line_key wins as identity (`lineKeySource: 'server'`).
 */
export function attachHydrateLineKeys(
  req: Request,
  snapshots: DdWorkflowLineSnapshot[]
): LineKeyAttachResult {
  const submissionSnaps = snapshots.filter(
    (s) => s.submission_uuid === req.submissionUuid || snapshots.length === 1
  )
  const failures: LineKeyAttachFailure[] = []

  if (req.isMaster) {
    const children = req.children.map((child, index) => {
      const payloadSlot = child.payloadSlot ?? slotForChildIndex(index)
      const snap = resolveSnapshotForSlot(submissionSnaps, payloadSlot, index)
      if (!snap?.line_key) {
        if (!child.lineKey) {
          failures.push({
            requestId: req.id,
            payloadSlot,
            sku: child.sku,
            reason: 'no_hydrate_snapshot',
          })
        }
        const source: LineKeySource =
          child.lineKeySource === 'fixture' ? 'fixture' : 'provisional'
        return withLineKey(
          child,
          child.lineKey ?? mintStoredLineKey(req.id, payloadSlot),
          child.lineKey ? (child.lineKeySource ?? source) : source,
          payloadSlot
        )
      }
      return withLineKey(
        child,
        snap.line_key,
        'server',
        snap.payload_slot ?? payloadSlot
      )
    })
    return { request: { ...req, children }, failures }
  }

  const single = req as Single
  const snap = resolveSnapshotForSlot(submissionSnaps, single.payloadSlot ?? 1, 0)
  if (!snap?.line_key) {
    if (!single.lineKey) {
      failures.push({
        requestId: req.id,
        payloadSlot: 1,
        sku: single.sku,
        reason: 'no_hydrate_snapshot',
      })
    }
    const source: LineKeySource =
      single.lineKeySource === 'fixture' ? 'fixture' : 'provisional'
    return {
      request: withLineKey(
        single,
        single.lineKey ?? mintStoredLineKey(req.id, 1),
        single.lineKey ? (single.lineKeySource ?? source) : source,
        single.payloadSlot ?? 1
      ),
      failures,
    }
  }
  return {
    request: withLineKey(
      single,
      snap.line_key,
      'server',
      snap.payload_slot ?? single.payloadSlot ?? 1
    ),
    failures,
  }
}

/** Fixture / demo rows with no server — mint locally in allocator shape. */
export function mintFixtureLineKeys(req: Single, source?: LineKeySource): Single
export function mintFixtureLineKeys(req: Master, source?: LineKeySource): Master
export function mintFixtureLineKeys(req: Request, source?: LineKeySource): Request
export function mintFixtureLineKeys(
  req: Request,
  source: LineKeySource = 'fixture'
): Request {
  const originRef = req.id
  if (req.isMaster) {
    return {
      ...req,
      children: req.children.map((child, index) =>
        withLineKey(
          child,
          mintStoredLineKey(originRef, slotForChildIndex(index)),
          source,
          slotForChildIndex(index)
        )
      ),
    }
  }
  const single = req as Single
  return withLineKey(single, mintStoredLineKey(originRef, 1), source, 1)
}

/**
 * Resolve display sku for a hydrate snapshot (pricing overlays still sku-keyed).
 * Identity match prefers lineKey, then payload_slot / ordinal.
 */
export function skuForSnapshot(req: Request, snap: DdWorkflowLineSnapshot): string | null {
  const byLineKey = findSubjectByLineKey(req, snap.line_key)
  if (byLineKey) return byLineKey.sku

  const slot =
    typeof snap.payload_slot === 'number' && snap.payload_slot > 0
      ? snap.payload_slot
      : typeof snap.line_ordinal === 'number' && snap.line_ordinal > 0
        ? snap.line_ordinal
        : null

  if (req.isMaster) {
    if (slot != null) {
      const bySlot = req.children.find((c) => c.payloadSlot === slot)
      if (bySlot) return bySlot.sku
      const atIndex = req.children[slot - 1]
      if (atIndex) return atIndex.sku
    }
    if (req.children.some((c) => c.sku === snap.line_key)) return snap.line_key
    if (req.children.length === 1) return req.children[0]!.sku
    return null
  }

  return (req as Single).sku
}
